import moment from "moment";
import max from "lodash/max";
import { compose } from "redux";
import range from "lodash/range";
import throttle from "lodash/throttle";
import memoize from "lodash/memoize";
import { Grid } from "@material-ui/core";
import React, { PureComponent } from "react";
import { withStyles } from "@material-ui/core/styles";

import { EMPTY_LIST } from "app/app.constants";
import { invokeIfFunction } from "utils/app.util";
import CalendarMonth from "app/components/CalendarYear/CalendarMonth";
import CalendarDaysHeader from "app/components/CalendarYear/CalendarDaysHeader";
import CalendarNavigation from "app/components/CalendarYear/CalendarNavigation";
import CalendarMonthsSidebar from "app/components/CalendarYear/CalendarMonthsSidebar";

class CalendarYear extends PureComponent {
  constructor(props) {
    super(props);

    const { year } = props;

    this.getMonthsMemoized = memoize(this.getMonths);

    this.state = {
      year,
      selectedDate: null,
      allMonths: this.getMonthsMemoized(year),
      numberOfDays: this.getMaxNumberOfDays(year, this.getMonthsMemoized(year)),
      startDate: moment({ year })
        .startOf("year")
        .startOf("isoWeek"),
    };

    this.setSelectedRangeThrottled = throttle(this.setSelectedRange, 100, {
      leading: true,
      trailing: true,
    });
  }

  getMonths = () => {
    const { numberOfMonths } = this.props;

    return range(0, numberOfMonths);
  };

  getMaxNumberOfDays = (year, months) => {
    return max(
      months.map(month => {
        const lastDayOfMonth = moment({ year, month }).endOf("month");
        const firstDayOfMonth = moment({ year, month })
          .startOf("month")
          .startOf("isoWeek");

        return lastDayOfMonth.diff(firstDayOfMonth, "days") + 1;
      }),
    );
  };

  getStartAndEndDate = year => {
    const { numberOfMonths } = this.props;

    return [
      moment({ year })
        .startOf("year")
        .startOf("isoWeek"),
      moment({ year })
        .add(numberOfMonths - 1, "months")
        .endOf("month"),
    ];
  };

  changeYear = year => {
    const { onYearChange } = this.props;

    const [startDate, endDate] = this.getStartAndEndDate(year);

    this.setState(
      {
        year,
        allMonths: this.getMonths(year),
        numberOfDays: this.getMaxNumberOfDays(
          year,
          this.getMonthsMemoized(year),
        ),
      },
      () => invokeIfFunction(onYearChange, startDate, endDate),
    );
  };

  setSelectedRange = (startDate, endDate, callback) => {
    const { selectedEndDate } = this.state;

    if (endDate === selectedEndDate) {
      return;
    }

    this.setState(
      {
        selectedEndDate: endDate,
        selectedStartDate: startDate,
      },
      callback,
    );
  };

  render() {
    const { year } = this.state;

    const {
      classes,
      dateRanges,
      DayComponent,
      onSelectDates,
      numberOfMonths,
      DateRangeComponent,
      DateRangeEndComponent,
      DateRangeStartComponent,
    } = this.props;

    return (
      <Grid container>
        <CalendarNavigation year={year} changeYear={this.changeYear} />
        <Grid container wrap="nowrap">
          <Grid item>
            <CalendarMonthsSidebar
              year={year}
              onSelectDates={onSelectDates}
              numberOfMonths={numberOfMonths}
              setSelectedRange={this.setSelectedRange}
            />
          </Grid>
          <Grid container item xs className={classes.calendarMonthsRoot}>
            <CalendarDaysHeader
              startDate={this.state.startDate}
              numberOfDays={this.state.numberOfDays}
            />
            {this.state.allMonths.map(month => {
              return (
                <CalendarMonth
                  year={year}
                  key={month}
                  month={month}
                  dateRanges={dateRanges}
                  DayComponent={DayComponent}
                  onSelectDates={onSelectDates}
                  DateRangeComponent={DateRangeComponent}
                  maxNumberOfDays={this.state.numberOfDays}
                  selectedEndDate={this.state.selectedEndDate}
                  DateRangeEndComponent={DateRangeEndComponent}
                  selectedStartDate={this.state.selectedStartDate}
                  setSelectedRange={this.setSelectedRangeThrottled}
                  DateRangeStartComponent={DateRangeStartComponent}
                />
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

CalendarYear.defaultProps = {
  numberOfMonths: 12,
  year: moment().year(),
  dateRanges: EMPTY_LIST,
};

const styles = () => ({
  calendarMonthsRoot: {
    overflow: "hidden",
  },
});

export default compose(withStyles(styles))(CalendarYear);
